import SeoLandingTemplate from "./SeoLandingTemplate";

export default function EventAccreditationSystems() {
  return (
    <SeoLandingTemplate
      keyword="Event Accreditation Systems"
      path="/event-accreditation-systems"
      title="Event Accreditation Systems — Access Control & Badging | OneTec"
      description="Event accreditation systems by OneTec. QR/RFID badging, multi-zone access control, live dashboards, offline fallback. From 500 to 50,000 attendees. Belgium-built, EU-deployed."
      serviceType="Event Accreditation Systems"
      hero={{
        eyebrow: "Service · Event Technology",
        headline: (
          <>
            Event accreditation
            <br />
            <span className="bg-ot-mint border-2 border-ot-fg px-2">systems.</span>
          </>
        ),
        subhead:
          "End-to-end event accreditation systems built in Belgium since 2002. From the registration form to the access scanner — QR/RFID badging, multi-zone control, live dashboards and full GDPR compliance. 500 to 50,000 attendees.",
      }}
      intro={[
        "Event accreditation systems are what stand between order and chaos at any meaningful event. They determine who gets in, where they can go, when they're authorised, and what data you collect along the way. OneTec has been building event accreditation systems in Belgium and across Europe since 2002 — for medical congresses, international tech summits, public exhibitions, government events and brand activations.",
        "Our event accreditation systems are modular by design. The registration layer captures attendee data with full GDPR compliance and issues a unique QR/RFID code. The badging layer prints PVC or paper badges on-demand at our award-winning self check-in kiosks. The access control layer scans those badges at zone entrances and enforces multi-zone rules in real time. The reporting layer surfaces live dashboards: who's in, who's out, peak times, zone utilisation, no-shows.",
        "We've built event accreditation systems for events of every size. 500 attendees in a single-room conference: a single kiosk and a tablet scanner is enough. 50,000 attendees across 12 zones over 5 days: multiple kiosk rows, dozens of zone scanners, redundant networks, on-call engineers. Same software stack, same standards, scaled by adding hardware. Because the worst time to discover your accreditation system can't scale is when it can't.",
      ]}
      benefits={[
        {
          title: "Modular, end-to-end",
          body:
            "Registration → badging → access control → reporting. One integrated event accreditation system, one vendor, one phone number when things heat up.",
        },
        {
          title: "Multi-zone logic",
          body:
            "Define unlimited zones, assign attendee tiers (VIP, Press, Staff, Exhibitor, General), enforce rules in real time at every scanner.",
        },
        {
          title: "QR + RFID + UHF",
          body:
            "Choose your scanning tech: QR for budget, RFID for speed, UHF for long-range walk-through. Or combine all three.",
        },
        {
          title: "Live attendance",
          body:
            "Real-time attendance dashboards exportable to your client. Peak-time heatmaps, zone utilisation, dwell-time analytics.",
        },
        {
          title: "Offline fallback",
          body:
            "Every scanner and kiosk holds a local cache of the access matrix. Network down? Scans continue. Sync resumes automatically.",
        },
        {
          title: "Full GDPR",
          body:
            "Belgian SRL, EU-only data, granular consent capture, audit logs, deletion on request. Privacy-by-design, not bolted on.",
        },
      ]}
      features={[
        "Branded online registration with payments",
        "Self check-in kiosks (PVC + paper badge printing)",
        "Assisted check-in stations for VIP / press",
        "QR / RFID / UHF badge encoding",
        "Multi-zone access control with tier rules",
        "Handheld scanners + tablet scanners + turnstile integration",
        "Real-time attendance dashboard",
        "Live zone heatmaps and dwell-time analytics",
        "Offline-mode failover with auto-sync",
        "GDPR-compliant data capture, storage and deletion",
      ]}
      useCases={[
        { title: "Medical Congresses", body: "Multi-track, multi-day, certified attendance reporting." },
        { title: "Tech Summits", body: "Multi-zone, multi-tier, sponsor lead tracking." },
        { title: "Public Exhibitions", body: "Turnstile integration, real-time capacity control." },
        { title: "Government Events", body: "Press, dignitary, public tiers with strict access rules." },
      ]}
      faqs={[
        {
          q: "What size events do your accreditation systems support?",
          a: "Every size. We've delivered event accreditation systems for 200-attendee board meetings and for 50,000-attendee public exhibitions. The software is the same; we scale by adding hardware.",
        },
        {
          q: "Can your event accreditation system enforce multi-zone access?",
          a: "Yes — multi-zone access control is core to our event accreditation stack. Define unlimited zones, assign attendee tiers (VIP, Press, Staff, Exhibitor, General Admission), and enforce rules in real time at every scanner. Rules can be time-based too.",
        },
        {
          q: "Do your scanners work offline?",
          a: "Yes. Every scanner and kiosk holds a local cache of the access matrix and attendee list. If the venue network fails mid-event, scans continue offline and sync automatically when the network is restored.",
        },
        {
          q: "Is the accreditation data GDPR-compliant?",
          a: "Yes. OneTec is a Belgian SRL operating under EU GDPR. Data is stored in EU-only data centers, attendee consent is captured explicitly at registration, full audit logs are maintained, and deletion-on-request is supported. Read our Privacy Policy for details.",
        },
        {
          q: "Can we integrate with our own CRM?",
          a: "Yes. We expose REST APIs for registration data export and accept incoming API calls to push attendee lists. Native integrations exist for HubSpot, Salesforce, Mailchimp and several Belgian/EU CRMs.",
        },
      ]}
      relatedProducts={[
        { slug: "access-control", name: "Access Control" },
        { slug: "onsite-registration-badging", name: "Onsite Registration & Badging" },
        { slug: "online-registration", name: "Online Registration" },
        { slug: "lead-trackers", name: "Lead Trackers" },
      ]}
    />
  );
}
